import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/auth/AuthContext";
import ResponsibilityModal from "@/components/ResponsibilityModal";
import AuthCallback from "@/auth/AuthCallback";
import ProtectedRoute from "@/auth/ProtectedRoute";
import RoleRoute from "@/auth/RoleRoute";

import Landing from "@/pages/PublicLanding";
import AdminCockpit from "@/pages/cockpits/AdminCockpit";
import ManagerPortfolio from "@/pages/cockpits/ManagerPortfolio";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import SchemeSettings from "@/pages/SchemeSettings";
import SchemeMembers from "@/pages/SchemeMembers";
import InviteAccept from "@/pages/InviteAccept";
import Demo from "@/pages/Demo";
import { ComplianceList, ComplianceDetail } from "@/pages/Compliance";
import Meetings from "@/pages/Meetings";
import MeetingDetail from "@/pages/MeetingDetail";
import Documents from "@/pages/Documents";
import Levies from "@/pages/Levies";
import Messages from "@/pages/Messages";
import Ask from "@/pages/Ask";
import OwnerPortal from "@/pages/OwnerPortal";
import Insurance from "@/pages/Insurance";
import InsuranceDetail from "@/pages/InsuranceDetail";
import AdminTemplates from "@/pages/AdminTemplates";
import Billing from "@/pages/Billing";
import PasswordReset from "@/pages/PasswordReset";
import About from "@/pages/About";
import AdminEmailLog from "@/pages/AdminEmailLog";
import AdminScheduler from "@/pages/AdminScheduler";
import AdminJurisdictions from "@/pages/AdminJurisdictions";
import AdminLegislationVersions from "@/pages/AdminLegislationVersions";
import AdminConnectors from "@/pages/AdminConnectors";
import GmailConnect from "@/pages/GmailConnect";
import EmailInbox from "@/pages/EmailInbox";
import Bylaws from "@/pages/Bylaws";
import BylawDetail from "@/pages/BylawDetail";
import BylawNew from "@/pages/BylawNew";

import SPDashboard from "@/pages/sp/Dashboard";
import SPLeads from "@/pages/sp/Leads";
import SPLeadDetail from "@/pages/sp/LeadDetail";
import SPProfile from "@/pages/sp/Profile";

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  // Service Providers have a dedicated app surface — never land on chair dashboard.
  if (user.system_role === "service_provider") {
    return <Navigate to="/sp/dashboard" replace />;
  }
  // Platform admin → admin cockpit
  if (user.system_role === "platform_admin") {
    return <Navigate to="/admin/home" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

// `NonSP` wraps any chair/owner/admin route — hard-redirects service_provider users
// to /sp/dashboard. Always runs after auth.
const NonSP = ({ children }) => (
  <ProtectedRoute>
    <RoleRoute allow="non-sp">{children}</RoleRoute>
  </ProtectedRoute>
);

// `SPOnly` gates the /sp/* surface to service_provider users only.
const SPOnly = ({ children }) => (
  <ProtectedRoute>
    <RoleRoute allow="sp">{children}</RoleRoute>
  </ProtectedRoute>
);

function RootRouter() {
  const location = useLocation();
  // Synchronous check: if the Emergent Google flow drops a session_id hash
  // anywhere, route to /auth/callback to consume it before any other route.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Chair / Owner / Admin app — SPs hard-redirect away. */}
      <Route path="/onboarding" element={<NonSP><Onboarding /></NonSP>} />
      <Route path="/dashboard" element={<NonSP><Dashboard /></NonSP>} />
      <Route path="/scheme/settings" element={<NonSP><SchemeSettings /></NonSP>} />
      <Route path="/scheme/members" element={<NonSP><SchemeMembers /></NonSP>} />
      <Route path="/scheme/compliance" element={<NonSP><ComplianceList /></NonSP>} />
      <Route path="/scheme/compliance/:item_id" element={<NonSP><ComplianceDetail /></NonSP>} />
      <Route path="/scheme/meetings" element={<NonSP><Meetings /></NonSP>} />
      <Route path="/scheme/meetings/:meeting_id" element={<NonSP><MeetingDetail /></NonSP>} />
      <Route path="/owner" element={<NonSP><OwnerPortal /></NonSP>} />
      <Route path="/scheme/documents" element={<NonSP><Documents /></NonSP>} />
      <Route path="/scheme/levies" element={<NonSP><Levies /></NonSP>} />
      <Route path="/scheme/messages" element={<NonSP><Messages /></NonSP>} />
      <Route path="/scheme/ask" element={<NonSP><Ask /></NonSP>} />
      <Route path="/scheme/insurance" element={<NonSP><Insurance /></NonSP>} />
      <Route path="/scheme/insurance/:policy_id" element={<NonSP><InsuranceDetail /></NonSP>} />
      <Route path="/scheme/billing" element={<NonSP><Billing /></NonSP>} />
      <Route path="/scheme/bylaws" element={<NonSP><Bylaws /></NonSP>} />
      <Route path="/scheme/bylaws/new" element={<NonSP><BylawNew /></NonSP>} />
      <Route path="/scheme/bylaws/:bylaw_id" element={<NonSP><BylawDetail /></NonSP>} />
      <Route path="/reset/:token" element={<PasswordReset />} />
      <Route path="/about" element={<About />} />
      <Route path="/admin/email-log" element={<NonSP><AdminEmailLog /></NonSP>} />
      <Route path="/admin/scheduler" element={<NonSP><AdminScheduler /></NonSP>} />
      <Route path="/admin/compliance/templates" element={<NonSP><AdminTemplates /></NonSP>} />
      <Route path="/admin/jurisdictions" element={<NonSP><AdminJurisdictions /></NonSP>} />
      <Route path="/admin/legislation-versions" element={<NonSP><AdminLegislationVersions /></NonSP>} />
      <Route path="/admin/connectors" element={<NonSP><AdminConnectors /></NonSP>} />
      <Route path="/admin/home" element={<NonSP><AdminCockpit /></NonSP>} />
      <Route path="/portfolio" element={<NonSP><ManagerPortfolio /></NonSP>} />
      <Route path="/scheme/settings/connectors/gmail" element={<NonSP><GmailConnect /></NonSP>} />
      <Route path="/scheme/messaging/email" element={<NonSP><EmailInbox /></NonSP>} />

      {/* Service Provider app — gated to system_role=service_provider. */}
      <Route path="/sp" element={<Navigate to="/sp/dashboard" replace />} />
      <Route path="/sp/dashboard" element={<SPOnly><SPDashboard /></SPOnly>} />
      <Route path="/sp/leads" element={<SPOnly><SPLeads /></SPOnly>} />
      <Route path="/sp/leads/:lead_id" element={<SPOnly><SPLeadDetail /></SPOnly>} />
      <Route path="/sp/profile" element={<SPOnly><SPProfile /></SPOnly>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RootRouter />
          <ResponsibilityModal />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
